import { TypographyH4, TypographyP } from '@talentmesh/ui/components/typography';
import { useState } from 'react';
import { CompanyImage } from '@/Clients/CompanyClient';
import { ImageCrop, ImageCropProps } from '@/Components/ImageCrop/ImageCrop';
import { ImageCropStrings, defaultAllowedImageTypes } from '@/Components/ImageCrop/ImageCropUtils';
import { useCompanyContext } from '@/Context/CompanyContext';
import { useNotificationContext } from '@/Context/NotificationContext';
import { useCompanyClient } from '@/Hooks/ClientHooks';
import useCompanyId from '@/Hooks/UseCompanyId';
import UIStrings from '@/Utils/UIStrings';

interface CompanyUploadImageSectionProps extends Omit<
    ImageCropProps,
    'strings' | 'validateCallback' | 'uploadCallbackAsync' | 'resetCallbackAsync' | 'processingErrorCallback'
> {
    title: string;
    description: string;
    companyImageType: CompanyImage;
    className?: string;
}

const CompanyUploadImageSection = ({
    className,
    src,
    title,
    aspect,
    description,
    circularCrop,
    companyImageType,
}: CompanyUploadImageSectionProps) => {
    const { refreshCurrentCompanyProfile } = useCompanyContext();
    const companyId = useCompanyId();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const companyClient = useCompanyClient();
    const [validationMessage, setValidationMessage] = useState<string>('');

    const imageCropStrings: ImageCropStrings = {
        selectButtonTooltipText: UIStrings.UploadImage,
        removeButtonTooltipText: UIStrings.RemoveImage,
        uploadButtonText: UIStrings.Upload,
        dialogHeaderText: UIStrings.CropYourImage,
        dialogContentText: UIStrings.SelectTheAreaToBeCropped,
    };

    const validateCallback = (value: Blob): boolean => {
        if (value.size > 1024 * 1024 * 1) {
            setValidationMessage(UIStrings.DefaultImageSizeValidationErrorText);
            return false;
        }

        if (defaultAllowedImageTypes.find((imageType) => imageType === value.type) === undefined) {
            setValidationMessage(UIStrings.DefaultImageTypeValidationErrorText);
            return false;
        }

        if (validationMessage) {
            setValidationMessage('');
        }

        return true;
    };

    const processingErrorCallback = () => {
        showFailToaster(UIStrings.DefaultImageCropDialogProcessingErrorText);
    };

    const handleImageChange = async (image?: Blob): Promise<void> => {
        try {
            if (image) {
                await companyClient.updateCompanyImage(companyId, image, companyImageType);
            } else {
                await companyClient.resetCompanyImage(companyId, companyImageType);
            }
            await refreshCurrentCompanyProfile(companyId);
            showSuccessToaster(UIStrings.SuccessfulImageChangeToaster);
        } catch (error) {
            showFailToaster(image ? UIStrings.FailedImageUploadToaster : UIStrings.FailedImageResetToaster);
        }
    };

    return (
        <div data-cy={`CompanyUpload${companyImageType}Section`} className="flex flex-row items-center justify-between">
            <div className="flex max-w-md flex-1 flex-col gap-1.5 pr-4">
                <TypographyH4>{title}</TypographyH4>
                <TypographyP>{description}</TypographyP>
                {validationMessage && <TypographyP className="text-destructive">{validationMessage}</TypographyP>}
            </div>
            <ImageCrop
                circularCrop={circularCrop}
                aspect={aspect}
                src={src}
                strings={imageCropStrings}
                validateCallback={validateCallback}
                uploadCallbackAsync={handleImageChange}
                resetCallbackAsync={handleImageChange}
                processingErrorCallback={processingErrorCallback}
                className={className}
            />
        </div>
    );
};

export default CompanyUploadImageSection;
